// Pricing/index.jsx — "/pricing".
// One page, two audiences, switched at the top: the self-serve plans a job
// seeker can compare and buy, or the institution offer, which is quoted
// rather than listed. Both branches run the same shape — offer, how pricing
// works, questions, closing panel — so the page keeps its weight whichever
// tab is on. Carries the .home-page class, so palette and type scale come
// from homePage.css like every other page.
import { useState } from "react";
import { ArrowRight, Check, GraduationCap, Layers, Plus, Sparkles } from "lucide-react";

import { FadeIn } from "../ui/fade-in";
import Seo from "../../seo/Seo";

import "../HomePage/homePage.css";
import "./Pricing.css";

const SIGNUP_URL = "https://app.raincareers.com/auth?mode=signUp";
const BOOK_URL = "/career-centers#book";
const CONTACT = "adminrain@raincareers.com";

/* The page's primary split. Billing period is a second, smaller switch that
   only exists inside the job seeker view — both ride the same control row. */
const AUDIENCES = [
  { id: "seekers", label: "Job seekers" },
  { id: "institutions", label: "Institutions" },
];

const FREQUENCIES = [
  { id: "monthly", label: "Monthly" },
  { id: "annual", label: "Annual", save: "Save 20%" },
];

const PLANS = [
  {
    id: "free",
    name: "Free",
    description: "Everything you need to run the search on your own.",
    price: { monthly: 0, annual: 0 },
    caption: { monthly: "Free to start", annual: "Free to start" },
    featuresHead: "What you get",
    features: [
      "Search and filter open roles",
      "Save and track your applications",
      "Networking tracking and recommended connections",
      "Career path exploration",
      "Limited interview prep",
      "Articles, tips and best practices",
    ],
    cta: "Get started",
  },
  {
    id: "all-access",
    name: "All access",
    description: "The AI tools on top, for when you are serious about your search.",
    price: { monthly: 14.99, annual: 11.99 },
    caption: {
      monthly: "Billed monthly, cancel whenever",
      annual: "Billed once a year at $143.88",
    },
    featuresHead: "Everything in Free, plus",
    features: [
      "AI resume builder and reviewer",
      "AI cover letter builder and reviewer",
      "AI LinkedIn reviewer",
      "AI mock interviews",
      "Career exploration assessments",
      "Automated outreach emails, with Gmail credits",
      "An AI assistant alongside all of it",
    ],
    cta: "Get started",
    badge: "Most popular",
    featured: true,
  },
];

/* The three things people actually want to know once they have seen the two
   numbers, kept short enough to read in a glance. */
const ASSURANCES = [
  {
    icon: Sparkles,
    title: "Free is not a trial",
    body: "Search, track and network for as long as you like without paying anything.",
  },
  {
    icon: Layers,
    title: "One subscription, every tool",
    body: "All access covers all of RAIN's AI. No add-ons, no per-feature pricing.",
  },
  {
    icon: GraduationCap,
    title: "Your school may cover it",
    body: "Partner campuses choose how much of RAIN to cover. Ask your career center what yours includes.",
  },
];

const INSTITUTION = [
  "All access for every student on campus",
  "Engagement and first-destination analytics",
  "Advisor dashboards and reporting",
  "Institution-wide deployment",
  "Designated success manager",
  "Custom integrations and SSO",
];

/* Quoted rather than listed, so the section explains the quote instead of
   pretending to be a third price. */
const STEPS = [
  {
    title: "Tell us your size",
    body: "Enrollment, and which parts of campus you want covered. That is most of the conversation.",
  },
  {
    title: "We quote one rate",
    body: "A single institutional rate based on your student body, and every student on campus is covered by it.",
  },
  {
    title: "You are live in two weeks",
    body: "We handle configuration, branding, guardrails and staff training. Most schools are fully live inside two weeks.",
  },
];

const FAQS = {
  seekers: [
    {
      q: "What is the difference between Free and All access?",
      a: "Free covers the search itself: finding roles, tracking applications, following up on networking and exploring career paths. All access adds the AI on top: the resume and cover letter builders, the LinkedIn reviewer, mock interviews, exploration assessments, automated outreach and the assistant that sits alongside all of it.",
    },
    {
      q: "How does annual billing work?",
      a: "Annual is billed once at $143.88 for the year, which works out to $11.99 a month, 20% less than paying month to month. Monthly is $14.99 and can be cancelled whenever you like.",
    },
    {
      q: "My school works with RAIN. Do I still pay?",
      a: "It depends on what your school has purchased. Partner institutions choose how much of RAIN to cover for their students, so what comes included varies from campus to campus. Sign in with your school account to see what is already unlocked, or ask your career center what your school covers.",
    },
    {
      q: "Can I start on Free and upgrade later?",
      a: "You can, and most students do not stay on Free for long. Free is there while you are still working out what you want. The moment you are actually applying, All access is the part that does the work: the resumes, the outreach, the interview practice. Everything you have already saved and tracked comes with you when you upgrade.",
    },
  ],
  institutions: [
    {
      q: "How is institutional pricing calculated?",
      a: "Pricing is based on the size of your student body, quoted as one institutional rate rather than a seat count you have to manage. It is designed to replace several point-solution contracts rather than add to them. Most partners consolidate three to four existing subscriptions into a single line item.",
    },
    {
      q: "How long does onboarding take?",
      a: "Most schools are fully live within two weeks. We handle platform configuration, branding, resume template uploads, AI guardrail alignment and staff training, and we support the launch with student-facing materials and a rollout plan built around your academic calendar.",
    },
    {
      q: "Does the rate cover every student, or only the ones who sign in?",
      a: "Every student. The rate is based on the size of your student body rather than a seat count, so there is nothing to meter and no licenses to reclaim at the end of the year. Adoption changes what you get out of RAIN, not what you pay for it.",
    },
    {
      q: "Do you support SSO?",
      a: "Yes. RAIN supports SSO with your institution's identity provider, so students and staff sign in with the credentials they already use on campus.",
    },
  ],
};

const money = (n) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

const Pricing = () => {
  const [audience, setAudience] = useState("seekers");
  const [frequency, setFrequency] = useState("monthly");
  const [openFaq, setOpenFaq] = useState(0);

  const isSeekers = audience === "seekers";

  const pickAudience = (id) => {
    setAudience(id);
    /* The two lists are different questions, so the open one resets rather
       than leaving an unrelated answer expanded. */
    setOpenFaq(0);
  };

  return (
    <div className="home-page pricing-page">
      <Seo path="/pricing" />
      {/* ---- Head ----
         Left-aligned like every other sub-page head on the site. */}
      <section className="pr-head">
        <div className="home-container">
          {/* The header entrance the ambassador, about and student pages
              use — stepped so the block arrives in sequence. */}
          <FadeIn delay={0}>
            <span className="pr-eyebrow">Pricing</span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="pr-title">Pricing that fits your search</h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="pr-sub">
              Free to explore, track and apply. Add the AI tools when
              you&rsquo;re ready, or see what your school already
              covers.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ---- The one control row ----
         Audience on the left, billing period on the right, both on the same
         rule. Two stacked pill tracks read as a control panel; one line of
         chips over a hairline is the filter row the resources page uses. */}
      <section className="pr-switch">
        <FadeIn delay={0.3} className="home-container pr-switch-row">
          <div
            className="pr-chips"
            role="tablist"
            aria-label="Who this pricing is for"
          >
            {AUDIENCES.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={audience === id}
                className={`pr-chip${audience === id ? " is-on" : ""}`}
                onClick={() => pickAudience(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {isSeekers && (
            <div
              className="pr-chips pr-chips-billing"
              role="tablist"
              aria-label="Billing period"
            >
              {FREQUENCIES.map(({ id, label, save }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={frequency === id}
                  className={`pr-chip is-quiet${
                    frequency === id ? " is-on" : ""
                  }`}
                  onClick={() => setFrequency(id)}
                >
                  {label}
                  {save && <span className="pr-save">{save}</span>}
                </button>
              ))}
            </div>
          )}
        </FadeIn>
      </section>

      {/* ---- Offer ---- */}
      <section className="pr-offer">
        <FadeIn delay={0.4} className="home-container">
          {isSeekers ? (
            <ul className="pr-grid">
              {PLANS.map((plan) => (
                <li
                  className={`pr-card${plan.featured ? " is-featured" : ""}`}
                  key={plan.id}
                >
                  <div className="pr-card-head">
                    <h2 className="pr-name">{plan.name}</h2>
                    {plan.badge && (
                      <span className="pr-badge">{plan.badge}</span>
                    )}
                  </div>

                  {/* Reserved at two lines, so both cards drop their price
                      onto the same baseline however long the copy runs. */}
                  <p className="pr-desc">{plan.description}</p>

                  <p className="pr-price">
                    <span className="pr-amount">
                      {money(plan.price[frequency])}
                    </span>
                    <span className="pr-per">/month</span>
                  </p>

                  <p className="pr-billed">{plan.caption[frequency]}</p>

                  <a
                    className={`pr-cta${plan.featured ? " is-solid" : ""}`}
                    href={SIGNUP_URL}
                  >
                    {plan.cta}
                    <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
                  </a>

                  <p className="pr-features-head">{plan.featuresHead}</p>

                  <ul className="pr-features">
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          ) : (
            /* Deliberately not a third card: schools are quoted, so putting
               them beside a free tier only invites the wrong comparison.
               Copy left, the inclusions right — the career centre page's row. */
            <div className="pr-inst">
              <div className="pr-inst-copy">
                <span className="pr-inst-eyebrow">For career centers</span>

                <h2 className="pr-inst-title">
                  One rate.
                  <br />
                  Every student covered.
                </h2>

                <p className="pr-inst-body">
                  What you pay depends on the size of your student body, and
                  every student on campus is included in it. We&rsquo;ll walk
                  you through the platform and quote from there.
                </p>

                <a className="pr-btn" href={BOOK_URL}>
                  Book a walkthrough
                  <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
                </a>
              </div>

              <div className="pr-inst-panel">
                <p className="pr-inst-head">What the rate includes</p>

                <ul className="pr-inst-list">
                  {INSTITUTION.map((item) => (
                    <li key={item}>
                      <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </FadeIn>
      </section>

      {/* ---- How pricing works ----
         Same band on both tabs — three hairline-topped columns under a
         left-aligned title — so switching audience doesn't change the
         page's shape, only what the columns say. */}
      <section className="pr-works">
        <div className="home-container">
          <h2 className="pr-works-title">How pricing works</h2>

          <ol className="pr-works-grid">
            {isSeekers
              ? ASSURANCES.map(({ icon: Icon, title, body }) => (
                  <li key={title}>
                    <span className="pr-works-mark" aria-hidden="true">
                      <Icon size={18} strokeWidth={2.1} />
                    </span>
                    <h3 className="pr-works-head">{title}</h3>
                    <p className="pr-works-body">{body}</p>
                  </li>
                ))
              : STEPS.map(({ title, body }, i) => (
                  <li key={title}>
                    <span className="pr-works-mark">{`0${i + 1}`}</span>
                    <h3 className="pr-works-head">{title}</h3>
                    <p className="pr-works-body">{body}</p>
                  </li>
                ))}
          </ol>
        </div>
      </section>

      {/* ---- Questions, per audience ---- */}
      <section className="pr-faq-section">
        <div className="home-container pr-faq-grid">
          <div>
            <span className="pr-eyebrow">Questions</span>

            <h2 className="pr-faq-title">
              Before you
              <br />
              pick a plan
            </h2>

            <p className="pr-faq-sub">
              Anything else? Reach us at{" "}
              <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
            </p>
          </div>

          <ul className="pr-faq-list">
            {FAQS[audience].map(({ q, a }, i) => {
              const isOpen = i === openFaq;

              return (
                <li key={q} className={`pr-faq-item${isOpen ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className="pr-faq-q"
                    aria-expanded={isOpen}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    <span>{q}</span>
                    <Plus size={18} strokeWidth={2} className="pr-faq-icon" />
                  </button>

                  {isOpen && <p className="pr-faq-a">{a}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ---- Closing panel ----
         The home page's soft grey sign-off, so the page ends the way the
         rest of the site does. */}
      <section className="pr-close">
        <div className="home-container">
          <div className="pr-close-panel">
            <h2 className="pr-close-title">
              {isSeekers
                ? "Start free. Upgrade when it counts."
                : "See what RAIN looks like on your campus."}
            </h2>

            <p className="pr-close-sub">
              {isSeekers
                ? "Set up an account in a couple of minutes and start tracking the search today. No card needed."
                : "Thirty minutes with our founders, shaped around your students, your staffing and your calendar."}
            </p>

            <a className="pr-btn" href={isSeekers ? SIGNUP_URL : BOOK_URL}>
              {isSeekers ? "Create your account" : "Book a walkthrough"}
              <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
            </a>

            <p className="pr-close-note">
              {isSeekers
                ? "Free to start. Upgrade any time."
                : "Most schools are live within two weeks."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
